import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SignInForm } from './components/login';
import { SignInFormPass } from './components/forgotPassword.js';
import { SetNewPassword } from './components/setNewPassword.js';
import Profile from './components/Profile.tsx';
import Fleet from './components/Fleet.tsx';
import PumpDashboard from './components/PumpDashboard.tsx';
import PumpEff from './components/PumpEff.tsx';
import FleetMonitor from './components/FleetMonitor.tsx';
import Settings from './components/Settings.tsx';
import UsersManagement from './components/UsersManagement.tsx';
import ClientsManagement from './components/ClientsManagement.tsx';
import './App.css';
import AuthRequired from './components/AuthRequired.tsx';
import { AuthProvider } from './context/AuthProvider.tsx';
import { ThemeProvider } from './context/ThemeProvider.tsx';
import { SidebarProvider } from './context/SidebarProvider.tsx';
import UserNotProvisioned from './components/UserNotProvisioned.tsx';
import AuthCallback from './components/AuthCallback.tsx';
import { Toaster } from 'sonner';
import useTheme from './hooks/useTheme.ts';
import AssetView from './components/AssetView.tsx';
import InterlocksSearch from './components/InterlocksSearch.tsx';
import ChartPopout from './components/ChartPopout.tsx';
import BlenderChartPopout from './components/BlenderChartPopout.tsx';
import FlaggedReengagementEvents from './components/FlaggedReengagementEvents.tsx';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient();

function ThemedToaster() {
  const { theme } = useTheme();

  return (
    <Toaster
      position="top-right"
      closeButton
      duration={4000}
      theme={theme === 'dark' ? 'dark' : 'light'}
      toastOptions={{
        style: { fontFamily: 'inherit' },
        classNames: {
          success: 'sonner-success',
          error: 'sonner-error',
          warning: 'sonner-warning',
        },
      }}
    />
  );
}

function App() {
  return (
    <React.StrictMode>
      <ThemeProvider>
        <AuthProvider>
          <SidebarProvider>
            <QueryClientProvider client={queryClient}>
              <ThemedToaster />

              <Router>
                <div className="App">
                  <Routes>

                    {/* Public Routes */}
                    <Route path="/sign-in" element={<SignInForm />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/forgot-password" element={<SignInFormPass />} />
                    <Route path="/reset-password" element={<SetNewPassword />} />
                    <Route path="/user-not-provisioned" element={<UserNotProvisioned />} />

                    <Route element={<AuthRequired />}>

                      <Route path="/" element={<Navigate to="/fleet" replace />} />

                      <Route path="/fleet" element={<Fleet />} />
                      <Route path="/pump-eff" element={<PumpEff />} />
                      <Route path="/pump-dashboard" element={<PumpDashboard />} />
                      <Route path="/monitor" element={<FleetMonitor />} />
                      <Route path="/asset-view" element={<AssetView />} />
                      <Route path="/interlocks" element={<InterlocksSearch />} />
                      <Route path="/chart-popout" element={<ChartPopout />} />
                      <Route path="/blender-chart-popout" element={<BlenderChartPopout />} />

                      {/* New Page */}
                      <Route
                        path="/flagged-reengagement-events"
                        element={<FlaggedReengagementEvents />}
                      />

                      <Route
                        path="/pad"
                        element={<div style={{ padding: '2rem' }}>Pad view coming soon...</div>}
                      />

                      <Route
                        path="/well"
                        element={<div style={{ padding: '2rem' }}>Well view coming soon...</div>}
                      />

                      <Route
                        path="/asset"
                        element={<div style={{ padding: '2rem' }}>Asset view coming soon...</div>}
                      />

                      <Route path="/settings" element={<Settings />}>
                        <Route index element={<Navigate replace to="users" />} />
                        <Route path="users" element={<UsersManagement />} />
                        <Route path="clients" element={<ClientsManagement />} />
                      </Route>

                      <Route path="/profile" element={<Profile />} />

                      <Route path="*" element={<Navigate to="/sign-in" replace />} />

                    </Route>
                  </Routes>
                </div>
              </Router>

            </QueryClientProvider>
          </SidebarProvider>
        </AuthProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
}

export default App;